import React from 'react'
import { useSelector } from 'react-redux'
import { AnalyticsElement } from '@typesApp/Analytics'
import { CustomErrorAction } from '@typesApp/AppError'
import { useInteractionContext } from '@context/interaction-context/useInteractionContext'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { Button } from '@components/foundation/button/Button'
import { ButtonLink } from '@components/foundation/button-link/ButtonLink'
import { appErrorSelector } from '@redux/slices/workflowSlice'
import { injectAnalytics } from '@utils/analyticsAttributes'
import styles from '../styles/ErrorActions.module.scss'

type ErrorActionsProps = {
  ctaLabel?: string
}

export const ErrorActions: React.FC<ErrorActionsProps> = ({ ctaLabel }) => {
  const { pushFallbackInteraction } = useInteractionContext()
  const error = useSelector(appErrorSelector)
  const actions = error?.customActions

  if (!actions || !Array.isArray(actions) || !actions.length) {
    return <DefaultErrorCTA ctaLabel={ctaLabel} onClick={error?.ctaCallback} />
  }

  return (
    <div className={styles.container}>
      {actions.map((action, index) => {
        const onClick = () => {
          pushFallbackInteraction('fallback')
          action.onClick?.()
        }

        return (
          <CustomCTA
            key={`${action.label}${index}`}
            label={action.label}
            color={action.color}
            onClick={onClick}
            index={index + 1}
            asLink={action.asLink}
            underline={action.underline}
          />
        )
      })}
    </div>
  )
}

type CTAProps = CustomErrorAction & {
  index: number
}

type DefaultErrorCTAProps = {
  ctaLabel?: string
  onClick?: () => void
}

const DefaultErrorCTA: React.FC<DefaultErrorCTAProps> = ({ ctaLabel, onClick }) => {
  const { t } = useAppTranslation({ keyPrefix: 'error-page' })
  const { pushFallbackInteraction } = useInteractionContext()

  if (typeof onClick !== 'function') {
    return null
  }

  const handleClick = () => {
    pushFallbackInteraction('retry')
    onClick()
  }

  return <Button onClick={handleClick}>{ctaLabel ?? t('body.ctaLabel')}</Button>
}

const CustomCTA: React.FC<CTAProps> = props => {
  const { index, label, onClick, color, asLink, underline } = props

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick()
    }
  }

  return (
    <>
      {!asLink && (
        <Button
          color={color}
          type="button"
          onClick={handleClick}
          {...injectAnalytics(AnalyticsElement.MORE_INFO, { index })}
        >
          {label}
        </Button>
      )}
      {asLink && (
        <ButtonLink
          underline={underline}
          onClick={handleClick}
          className={styles.ctaLink}
          {...injectAnalytics(AnalyticsElement.MORE_INFO, { index })}
        >
          {label}
        </ButtonLink>
      )}
    </>
  )
}
